import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  Trash2,
  Mail,
  Phone,
  User,
  MessagesSquare,
  Save,
  Pencil,
  Loader2,
} from 'lucide-react'
import { inquiryAPI } from '../services/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

interface Inquiry {
  id: string
  reference: string
  name: string
  email: string
  phone?: string
  subject: string
  message: string
  status: 'new' | 'in_progress' | 'resolved' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  sessionId?: string
  conversationId?: any
  ipAddress?: string
  userAgent?: string
  source?: string
  assignedTo?: any
  response?: string
  responseDate?: string
  tags: string[]
  metadata?: { referrer?: string; deviceType?: string; location?: string }
  createdAt: string
  updatedAt: string
}

const STATUS_LABEL: Record<Inquiry['status'], string> = {
  new: 'New',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
}

// Variants describe what the value means for the reader, not its colour:
// something new or urgent needs attention, resolved does not.
const STATUS_VARIANT: Record<Inquiry['status'], 'default' | 'warning' | 'success' | 'muted'> = {
  new: 'default',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'muted',
}

const PRIORITY_VARIANT: Record<
  Inquiry['priority'],
  'muted' | 'secondary' | 'warning' | 'destructive'
> = {
  low: 'muted',
  medium: 'secondary',
  high: 'warning',
  urgent: 'destructive',
}

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 py-2">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="text-right font-medium break-all">{children}</span>
  </div>
)

const InquiryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [inquiry, setInquiry] = useState<Inquiry | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [status, setStatus] = useState<Inquiry['status']>('new')
  const [priority, setPriority] = useState<Inquiry['priority']>('medium')
  const [response, setResponse] = useState('')
  const [tags, setTags] = useState('')
  const [editingNotes, setEditingNotes] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await inquiryAPI.getById(id!)
        setInquiry(data)
        setStatus(data.status)
        setPriority(data.priority)
        setResponse(data.response ?? '')
        setTags((data.tags ?? []).join(', '))
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Could not load this inquiry')
        navigate('/inquiries', { replace: true })
      } finally {
        setLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const dirty =
    !!inquiry &&
    (status !== inquiry.status ||
      priority !== inquiry.priority ||
      response !== (inquiry.response ?? '') ||
      tags !== (inquiry.tags ?? []).join(', '))

  const save = async () => {
    if (!inquiry) return
    setSaving(true)
    try {
      await inquiryAPI.update(inquiry.id, {
        status,
        priority,
        // The API rejects an empty string for response, so send it only when set.
        ...(response.trim() ? { response: response.trim() } : {}),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      })
      setInquiry({
        ...inquiry,
        status,
        priority,
        response: response.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      })
      setEditingNotes(false)
      toast.success('Saved')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Could not save changes')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!inquiry) return
    if (!confirm(`Delete inquiry ${inquiry.reference}? This cannot be undone.`)) return
    try {
      await inquiryAPI.delete(inquiry.id)
      toast.success('Deleted')
      navigate('/inquiries')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Could not delete this inquiry')
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-2/3" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-72 lg:col-span-1" />
          <Skeleton className="h-72 lg:col-span-2" />
        </div>
      </div>
    )
  }

  if (!inquiry) return null

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/inquiries">
            <ArrowLeft />
            Back to inquiries
          </Link>
        </Button>

        <div className="flex items-center gap-2">
          {inquiry.conversationId && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/conversations/${inquiry.conversationId?._id ?? inquiry.conversationId}`}>
                <MessagesSquare />
                View conversation
              </Link>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={remove}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 />
            Delete
          </Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving}>
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            Save changes
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold leading-tight tracking-tight">{inquiry.subject}</h1>
          <Badge variant={STATUS_VARIANT[inquiry.status]}>{STATUS_LABEL[inquiry.status]}</Badge>
          <Badge variant={PRIORITY_VARIANT[inquiry.priority]}>{inquiry.priority}</Badge>
        </div>
        <p className="font-mono text-sm text-muted-foreground">{inquiry.reference}</p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:sticky lg:top-6 lg:col-span-1">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="font-medium">{inquiry.name}</span>
              </div>
              <a
                href={`mailto:${inquiry.email}`}
                className="flex items-center gap-2 break-all text-primary underline-offset-4 hover:underline"
              >
                <Mail className="h-4 w-4 shrink-0" />
                {inquiry.email}
              </a>
              {inquiry.phone && (
                <a
                  href={`tel:${inquiry.phone}`}
                  className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  {inquiry.phone}
                </a>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-border text-sm">
              <Row label="Received">{formatDate(inquiry.createdAt)}</Row>
              <Row label="Last updated">{formatDate(inquiry.updatedAt)}</Row>
              {inquiry.responseDate && <Row label="Responded">{formatDate(inquiry.responseDate)}</Row>}
              {inquiry.source && <Row label="Source">{inquiry.source}</Row>}
              {inquiry.metadata?.deviceType && <Row label="Device">{inquiry.metadata.deviceType}</Row>}
              {inquiry.metadata?.referrer && <Row label="Referrer">{inquiry.metadata.referrer}</Row>}
              {inquiry.ipAddress && <Row label="IP address">{inquiry.ipAddress}</Row>}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Message</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-5 text-sm leading-7">
                {inquiry.message}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Handling</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as Inquiry['status'])}
                  >
                    {(Object.keys(STATUS_LABEL) as Inquiry['status'][]).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    id="priority"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as Inquiry['priority'])}
                  >
                    {(['low', 'medium', 'high', 'urgent'] as const).map((p) => (
                      <option key={p} value={p}>
                        {p[0].toUpperCase() + p.slice(1)}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Notes and tags read back as recorded values once saved.
                  Leaving them as permanently open inputs gave no signal that
                  anything had been stored — a saved note looked identical to
                  an empty form. */}
              {editingNotes ? (
                <div className="space-y-4 rounded-md border border-dashed p-4">
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags</Label>
                    <Input
                      id="tags"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="comma, separated, tags"
                    />
                    <p className="text-xs text-muted-foreground">
                      Tags are searchable from the inquiries list.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="response">Response note</Label>
                    <Textarea
                      id="response"
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="What was sent back, or what needs doing next."
                      className="min-h-[9rem]"
                      maxLength={2000}
                      autoFocus
                    />
                    <p className="text-xs text-muted-foreground">
                      {response.length}/2000 — an internal record. Nothing is emailed to the sender.
                    </p>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTags((inquiry.tags ?? []).join(', '))
                        setResponse(inquiry.response ?? '')
                        setEditingNotes(false)
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Tags</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {inquiry.tags?.length ? (
                        inquiry.tags.map((tag) => (
                          <Badge key={tag} variant="secondary">
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">Response note</span>
                      <Button variant="outline" size="sm" onClick={() => setEditingNotes(true)}>
                        <Pencil />
                        {inquiry.response || inquiry.tags?.length ? 'Edit' : 'Add note'}
                      </Button>
                    </div>

                    {inquiry.response ? (
                      <>
                        <div className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-4 text-sm leading-relaxed">
                          {inquiry.response}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Recorded {formatDate(inquiry.responseDate)} — internal only, not emailed.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Nothing recorded yet. Notes are for your own reference — replying still
                        happens from your email client.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default InquiryDetails
